import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../../lib/supabase';
import { logAudit } from '../../../../lib/audit';
import type { AdminRecord } from '../types';
import { roleLabel } from '../types';

interface PlanOption {
  id: string;
  name: string;
  slug: string;
  price_brl: number;
  accounts_limit: number;
  cards_limit: number;
  monthly_transactions_limit: number;
  goals_limit: number;
}

const fmtN = (n: number | null | undefined) => (n === -1 || n == null ? '∞' : String(n));

interface Props {
  admin: AdminRecord;
}

export default function AdminConvertToUserSection({ admin }: Props) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [plans, setPlans] = useState<PlanOption[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [converting, setConverting] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (!expanded || plans.length > 0) return;
    setLoadingPlans(true);
    supabase
      .from('plans')
      .select('id, name, slug, price_brl, accounts_limit, cards_limit, monthly_transactions_limit, goals_limit')
      .eq('is_active', true)
      .order('price_brl', { ascending: true })
      .then(({ data }) => {
        setPlans((data || []) as PlanOption[]);
        if ((data || []).length > 0) setSelectedPlanId((data as PlanOption[])[0].id);
        setLoadingPlans(false);
      });
  }, [expanded, plans.length]);

  const handleConvert = async () => {
    if (!selectedPlanId) return;
    setConverting(true);
    setMsg('');
    try {
      const chosen = plans.find((p) => p.id === selectedPlanId);
      if (!chosen) { setMsg('Plano não encontrado.'); setConverting(false); return; }

      // 1. Remove admin role
      const { error: roleErr } = await supabase
        .from('user_roles')
        .delete()
        .eq('id', admin.roleId);
      if (roleErr) throw roleErr;

      // 2. Assign selected plan via subscriptions
      const isFree = chosen.price_brl === 0;
      await supabase.from('subscriptions').upsert(
        {
          user_id: admin.userId,
          plan_id: selectedPlanId,
          status: 'active',
          subscription_type: isFree ? 'free' : 'paid',
          amount: chosen.price_brl,
          billing_cycle: 'monthly',
          started_at: new Date().toISOString(),
          auto_renew: !isFree,
        },
        { onConflict: 'user_id' }
      );

      // 3. Update profile plan field
      await supabase
        .from('profiles')
        .update({ plan: isFree ? 'free' : 'paid', updated_at: new Date().toISOString() })
        .eq('id', admin.userId);

      await logAudit({
        action: 'convert_admin_to_user',
        resource: 'admin',
        resource_id: admin.userId,
        details: { old_role: admin.role, assigned_plan: chosen.name },
      });

      navigate('/admin/usuarios');
    } catch (err: unknown) {
      setMsg('Erro: ' + (err instanceof Error ? err.message : 'Falha na conversão'));
      setConverting(false);
    }
  };

  return (
    <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl overflow-hidden">
      {/* Header toggler */}
      <button
        onClick={() => { setExpanded((v) => !v); setConfirm(false); setMsg(''); }}
        className="w-full flex items-center justify-between p-5 cursor-pointer hover:bg-amber-500/5 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-amber-500/20 rounded-xl">
            <i className="ri-user-follow-line text-amber-400 text-[15px]" />
          </div>
          <div className="text-left">
            <p className="text-[14px] font-600 text-amber-300">Converter em Usuário Comum</p>
            <p className="text-[12px] text-white/40 mt-0.5">
              Remove o papel <strong className="text-white/60">{roleLabel[admin.role]}</strong> e define o plano de usuário
            </p>
          </div>
        </div>
        <i className={`ri-arrow-${expanded ? 'up' : 'down'}-s-line text-amber-400/60 text-[18px]`} />
      </button>

      {expanded && (
        <div className="px-5 pb-5 border-t border-amber-500/15">
          {/* Warning */}
          <div className="mt-4 p-3 bg-amber-500/10 rounded-xl flex items-start gap-2.5 mb-5">
            <i className="ri-error-warning-line text-amber-400 text-[15px] mt-0.5 shrink-0" />
            <p className="text-[12px] text-amber-300/80 leading-relaxed">
              Após a conversão, este usuário <strong>perderá todo o acesso administrativo</strong> imediatamente e passará a ser um usuário comum com o plano selecionado.
            </p>
          </div>

          {/* Plan selection */}
          {loadingPlans ? (
            <div className="flex items-center gap-2 py-4 text-white/40 text-[13px]">
              <i className="ri-loader-4-line animate-spin" /> Carregando planos...
            </div>
          ) : (
            <>
              <p className="text-[11px] font-600 text-white/40 uppercase tracking-wider mb-3">Selecionar plano de destino</p>
              <div className="grid grid-cols-2 gap-2.5 mb-5">
                {plans.map((p) => {
                  const isSelected = selectedPlanId === p.id;
                  return (
                    <button
                      key={p.id}
                      onClick={() => { setSelectedPlanId(p.id); setConfirm(false); }}
                      className={`flex items-start justify-between p-3.5 rounded-xl border text-left transition-all cursor-pointer ${
                        isSelected
                          ? 'border-amber-500/50 bg-amber-500/10'
                          : 'border-white/8 bg-white/3 hover:bg-white/5'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-[13px] font-600 text-white">{p.name}</p>
                        </div>
                        <p className="text-[12px] text-white/50">
                          {p.price_brl === 0 ? 'Gratuito' : `R$ ${Number(p.price_brl).toFixed(2)}/mês`}
                        </p>
                        <p className="text-[11px] text-white/30 mt-1">
                          {fmtN(p.accounts_limit)} contas · {fmtN(p.cards_limit)} cartões · {fmtN(p.monthly_transactions_limit)} lançamentos
                        </p>
                      </div>
                      {isSelected && (
                        <i className="ri-checkbox-circle-fill text-amber-400 text-[16px] shrink-0 ml-2" />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Confirm step */}
              {!confirm ? (
                <button
                  onClick={() => setConfirm(true)}
                  disabled={!selectedPlanId}
                  className="w-full py-2.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 text-[13px] font-600 rounded-xl cursor-pointer whitespace-nowrap transition-colors border border-amber-500/30 disabled:opacity-40"
                >
                  <i className="ri-user-follow-line mr-2" />
                  Converter para usuário comum com plano selecionado
                </button>
              ) : (
                <div className="p-4 bg-red-600/10 border border-red-500/20 rounded-xl">
                  <p className="text-[13px] font-600 text-red-300 mb-1">
                    <i className="ri-error-warning-line mr-1.5" />Confirmar conversão?
                  </p>
                  <p className="text-[11px] text-white/40 mb-4">
                    <strong className="text-white/60">{admin.full_name}</strong> perderá o papel de <strong className="text-white/60">{roleLabel[admin.role]}</strong> e passará para o plano <strong className="text-white/60">{plans.find((p) => p.id === selectedPlanId)?.name}</strong>. Esta ação não pode ser desfeita facilmente.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setConfirm(false)}
                      className="flex-1 py-2 border border-white/10 text-white/60 text-[12px] font-600 rounded-xl hover:bg-white/5 cursor-pointer whitespace-nowrap transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleConvert}
                      disabled={converting}
                      className="flex-1 py-2 bg-red-600/30 hover:bg-red-600/50 text-red-300 text-[12px] font-600 rounded-xl cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50"
                    >
                      {converting ? (
                        <><i className="ri-loader-4-line animate-spin mr-1.5" />Convertendo...</>
                      ) : (
                        <><i className="ri-user-follow-line mr-1.5" />Confirmar conversão</>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {msg && (
                <p className={`text-[12px] mt-2 ${msg.includes('Erro') ? 'text-red-400' : 'text-emerald-400'}`}>{msg}</p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
